import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useAdmin } from "../../contexts/AdminContext";

export default function LotPage() {
  const { id } = useParams<{ id: string }>();
  const { settings } = useAdmin();
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState("");

  const prize = settings.prizes.find(p => p.id === Number(id));

  useEffect(() => {
    if (!prize && id) {
       // Check if it's the Grand Prix (id 1)
       if (id === "1") {
          navigate("/bien");
       } else {
          navigate("/");
       }
    }
    if (prize) {
      setActiveImg(prize.img);
    }
  }, [prize, id, navigate]);

  if (!prize) return null;

  const allImages = [prize.img, ...(prize.gallery || [])].filter(Boolean);

  return (
    <main className="min-h-screen bg-slate-50 font-inter">
      <Navbar />

      {/* Header & Breadcrumb */}
      <div className="bg-white border-b border-slate-100 pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="text-sm text-slate-400 mb-5 flex items-center gap-2">
            <Link to="/" className="hover:text-slate-700 transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-slate-300"></i>
            <span className="text-slate-700 font-medium">{prize.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`${prize.tagColor} text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-sm`}>
                  {prize.tag}
                </span>
                <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                  <i className="ri-shield-check-line text-xs"></i> Zertifiziert & Garantiert
                </span>
              </div>
              <h1 className="font-playfair text-3xl md:text-5xl font-black text-slate-900 mt-2">
                {prize.title}
              </h1>
            </div>
            <div className="flex-shrink-0 text-left md:text-right">
              <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Geschätzter Wert</p>
              <p className="font-playfair text-4xl font-bold text-primary">{prize.value}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Main Visuals & Content */}
          <div className="lg:col-span-8 space-y-10">
            
            {/* Gallery Section */}
            <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100 overflow-hidden">
              <div className="aspect-[16/9] w-full rounded-2xl overflow-hidden mb-4 bg-slate-100 group relative">
                <img 
                  src={activeImg} 
                  alt={prize.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              
              {allImages.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                   {allImages.map((img, i) => (
                      <button 
                         key={i} 
                         onClick={() => setActiveImg(img)}
                         className={`w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${activeImg === img ? 'border-primary scale-95 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'}`}
                      >
                         <img src={img} className="w-full h-full object-cover" alt={`Thumb ${i}`} />
                      </button>
                   ))}
                </div>
              )}
            </div>

            {/* Description Card */}
            <div className="bg-white rounded-[2rem] p-10 shadow-sm border border-slate-100">
              <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-6">Preispräsentation</h2>
              <div className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap">
                 {prize.description || "Zur Zeit ist keine Beschreibung verfügbar."}
              </div>
            </div>

            {/* Specs Grid */}
            <div className="bg-white rounded-[2rem] p-10 shadow-sm border border-slate-100">
               <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-8">Technische Spezifikationen</h2>
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {prize.specifications && prize.specifications.length > 0 ? (
                    prize.specifications.map((spec, i) => (
                      <div key={i} className="bg-slate-50 rounded-2xl p-5 border border-slate-100 group hover:border-orange-200 transition-colors">
                         <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                            <i className={`${spec.icon} text-xl text-primary`}></i>
                         </div>
                         <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider mb-1">{spec.label}</p>
                         <p className="text-slate-900 font-bold text-sm">{spec.value}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-sm">Informationen werden aktualisiert...</p>
                  )}
               </div>
            </div>
          </div>

          {/* Sidebar / CTA */}
          <div className="lg:col-span-4">
             <div className="sticky top-24 space-y-6">
                <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl overflow-hidden relative group">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl -mr-16 -mt-16 group-hover:bg-primary/20 transition-colors" />
                   
                   <p className="text-primary font-black text-xs uppercase tracking-[0.2em] mb-4">Bereit zu gewinnen?</p>
                   <h3 className="font-playfair text-2xl font-bold mb-6 leading-tight">Holen Sie sich jetzt Ihre Lose für diese Verlosung</h3>
                   
                   <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-3 text-white/70 text-sm">
                         <i className="ri-checkbox-circle-line text-primary"></i>
                         <span>Chance auf den {prize.tag}</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/70 text-sm">
                         <i className="ri-checkbox-circle-line text-primary"></i>
                         <span>Berechtigt zur VILLA-Verlosung</span>
                      </div>
                      <div className="flex items-center gap-3 text-white/70 text-sm">
                         <i className="ri-checkbox-circle-line text-primary"></i>
                         <span>Unterstützt eine wohltätige Organisation</span>
                      </div>
                   </div>

                   <Link 
                      to="/forfaits" 
                      className="block w-full bg-primary hover:bg-primary-dark text-white text-center py-4 rounded-2xl font-bold transition-all shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-[1.02]"
                   >
                      An der Verlosung teilnehmen
                   </Link>
                   
                   <p className="text-[10px] text-white/40 text-center mt-4">100% sichere Zahlung über Stripe & PayPal</p>
                </div>

                <div className="bg-white rounded-[2rem] p-6 border border-slate-100">
                   <h4 className="font-bold text-slate-900 text-sm mb-4">Eine zertifizierte Verlosung</h4>
                   <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                         <i className="ri-bank-line text-lg text-slate-800"></i>
                      </div>
                      <div className="flex-1">
                         <p className="text-[10px] font-bold text-slate-400 uppercase leading-none mb-1">Notarielle Aufsicht</p>
                         <p className="text-xs font-bold text-slate-900">{settings.bailiffName}</p>
                      </div>
                   </div>
                </div>
             </div>
          </div>

        </div>
      </div>

      <Footer />
    </main>
  );
}
